/**
 * Three reels, one letter each. The bit of the cabinet everyone remembers.
 *
 * ───────────────────────────────────────────────── verdict on the prior art ──
 *
 * `chess-tower-defence/src/ui/InitialsWheel.ts` solves the same problem and I
 * took the *shape* from it and none of the code. What it gets right is the
 * reading: three separate one-character reels, each spun in place, rather than
 * a text field pretending to be an arcade. That is the whole idea and it is
 * right.
 *
 * What it gets wrong is that it has no keyboard at all. Grep it: `touchstart`,
 * `touchmove`, `touchend`, `mousedown`, `wheel`. No `keydown`, no `tabindex`,
 * no `role`. On a desktop with a keyboard in front of you — the machine this
 * gesture was *invented* on — you cannot enter your initials without dragging
 * a div with the mouse. The brief's line was "an arcade wheel that needs a
 * mouse is not retro, it is broken", and that is the file it describes.
 *
 * The momentum goes too. It carries velocity, decay and a `requestAnimationFrame`
 * loop to fling a 36-item ring, and a player choosing three characters wants to
 * land on one, not to be thrown past it. One step per 22px of travel, no
 * inertia, releases where you left it.
 *
 * ─────────────────────────────────────────────────────────────── the gesture ──
 *
 * Keyboard — type the character and the reel takes it and advances, which is
 * what a keyboard is for; up/down spins the reel you are on; left/right walks
 * between reels; backspace walks back; Enter files the score.
 *
 * Touch — a caret above and below each reel, both at the 46px floor this
 * cabinet holds everywhere, and a vertical drag on the reel itself for anyone
 * who knows what the gesture is supposed to feel like.
 *
 * No `<input>` anywhere, which is the deliberate part: three text fields summon
 * the OS keyboard, and on a 390px phone that shoves the whole run-over sheet up
 * behind a grey slab at the moment the player most wants to read it.
 */

import { el, icon } from './Icon';
import { INITIALS_ALPHABET, normaliseInitials } from './HighScores';

/** CSS px of vertical drag that advances the reel one character. */
const DRAG_STEP = 22;

export interface InitialsEntryCallbacks {
  /** Enter, or the last reel taking a typed character. Never fires twice. */
  onSubmit?(initials: string): void;
  /** Any change to the three characters. */
  onChange?(initials: string): void;
}

interface Reel {
  root: HTMLElement;
  glyph: HTMLElement;
  index: number;
}

export class InitialsEntry {
  readonly root = el('div', 'initials');
  private reels: Reel[] = [];
  private active = 0;
  private dragging: { reel: number; lastY: number } | null = null;

  constructor(private readonly cb: InitialsEntryCallbacks = {}) {
    const rail = el('div', 'initials__rail');
    for (let i = 0; i < 3; i++) rail.append(this.buildReel(i));
    this.root.append(rail);
    this.root.addEventListener('keydown', this.onKey);
  }

  /** The three characters, always exactly three. */
  get value(): string {
    return this.reels.map((r) => INITIALS_ALPHABET[r.index]).join('');
  }

  set value(next: string) {
    const chars = normaliseInitials(next);
    this.reels.forEach((r, i) => {
      r.index = Math.max(0, INITIALS_ALPHABET.indexOf(chars[i]));
      this.paint(i);
    });
  }

  /** Puts the caret on the first reel and focuses it. */
  focus(): void {
    this.setActive(0);
    this.reels[0]?.glyph.focus();
  }

  destroy(): void {
    this.root.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  // ── building ──

  private buildReel(i: number): HTMLElement {
    const root = el('div', 'initials__reel');

    const up = el('button', 'btn btn--icon initials__step');
    up.type = 'button';
    up.tabIndex = -1;
    up.setAttribute('aria-label', `Previous character, slot ${i + 1}`);
    up.append(icon('angle-up', { size: 12 }));
    up.addEventListener('click', () => { this.setActive(i); this.step(i, -1); });

    const glyph = el('div', 'initials__glyph');
    glyph.tabIndex = 0;
    glyph.setAttribute('role', 'spinbutton');
    glyph.setAttribute('aria-label', `Initial ${i + 1} of 3`);
    glyph.addEventListener('focus', () => this.setActive(i));
    glyph.addEventListener('pointerdown', (ev) => this.onPointerDown(ev, i));
    glyph.addEventListener('pointermove', this.onPointerMove);
    glyph.addEventListener('pointerup', this.onPointerUp);
    glyph.addEventListener('pointercancel', this.onPointerUp);

    const down = el('button', 'btn btn--icon initials__step');
    down.type = 'button';
    down.tabIndex = -1;
    down.setAttribute('aria-label', `Next character, slot ${i + 1}`);
    down.append(icon('angle-down', { size: 12 }));
    down.addEventListener('click', () => { this.setActive(i); this.step(i, +1); });

    root.append(up, glyph, down);
    this.reels.push({ root, glyph, index: 0 });
    this.paint(i);
    return root;
  }

  // ── state ──

  private paint(i: number): void {
    const r = this.reels[i];
    if (!r) return;
    const ch = INITIALS_ALPHABET[r.index];
    r.glyph.textContent = ch;
    r.glyph.setAttribute('aria-valuenow', String(r.index));
    r.glyph.setAttribute('aria-valuetext', ch);
    r.root.classList.toggle('is-active', i === this.active);
  }

  private setActive(i: number): void {
    this.active = Math.min(2, Math.max(0, i));
    this.reels.forEach((_, n) => this.paint(n));
  }

  private step(i: number, by: number): void {
    const r = this.reels[i];
    if (!r) return;
    const n = INITIALS_ALPHABET.length;
    r.index = ((r.index + by) % n + n) % n;
    this.paint(i);
    this.cb.onChange?.(this.value);
  }

  /** Sets a reel to a typed character. Returns false if it is not in the set. */
  private put(i: number, ch: string): boolean {
    const at = INITIALS_ALPHABET.indexOf(ch.toUpperCase());
    if (at < 0) return false;
    const r = this.reels[i];
    if (!r) return false;
    r.index = at;
    this.paint(i);
    this.cb.onChange?.(this.value);
    return true;
  }

  // ── keyboard ──

  private readonly onKey = (ev: KeyboardEvent): void => {
    // Escape and Tab belong to the modal shell above; everything else that
    // lands here is aimed at the reels and is answered here or ignored.
    if (ev.key === 'Escape' || ev.key === 'Tab') return;

    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.cb.onSubmit?.(this.value);
      return;
    }
    if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
      ev.preventDefault();
      this.step(this.active, ev.key === 'ArrowUp' ? -1 : +1);
      return;
    }
    if (ev.key === 'ArrowLeft' || ev.key === 'Backspace') {
      ev.preventDefault();
      this.move(-1);
      return;
    }
    if (ev.key === 'ArrowRight') {
      ev.preventDefault();
      this.move(+1);
      return;
    }
    // A typed character fills the reel and walks on, the way the cabinet does.
    if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
      if (!this.put(this.active, ev.key)) return;
      ev.preventDefault();
      this.move(+1);
    }
  };

  /** Walks between reels, keeping DOM focus with the caret so both agree. */
  private move(by: number): void {
    const next = Math.min(2, Math.max(0, this.active + by));
    this.setActive(next);
    this.reels[next]?.glyph.focus();
  }

  // ── drag ──

  private onPointerDown(ev: PointerEvent, i: number): void {
    // Mouse keeps its click-through to the carets; a finger owns the reel.
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;
    this.setActive(i);
    this.reels[i]?.glyph.focus();
    this.dragging = { reel: i, lastY: ev.clientY };
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
  }

  private readonly onPointerMove = (ev: PointerEvent): void => {
    const d = this.dragging;
    if (!d) return;
    const dy = ev.clientY - d.lastY;
    if (Math.abs(dy) < DRAG_STEP) return;
    // Down the screen walks *back* through the alphabet, the way a physical
    // reel turns: the character you are pulling toward you is the earlier one.
    const steps = Math.trunc(dy / DRAG_STEP);
    for (let n = 0; n < Math.abs(steps); n++) this.step(d.reel, steps > 0 ? -1 : +1);
    d.lastY += steps * DRAG_STEP;
  };

  private readonly onPointerUp = (ev: PointerEvent): void => {
    if (!this.dragging) return;
    this.dragging = null;
    const node = ev.currentTarget as HTMLElement;
    if (node.hasPointerCapture(ev.pointerId)) node.releasePointerCapture(ev.pointerId);
  };
}
